import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Select from '@starbucks-web/pattern-library/lib/components/select';

import { NONE_VALUE } from '../../pdp-provider/transform-selected-options';

import useMoveFocus from './use-move-focus';
import messages from './messages';
import { commonMessages } from 'shared/app/messages';

const SelectWrapper = ({
  addNoneOption,
  children,
  id,
  isDemystifyMenuPdpEnabled,
  modified,
  options,
  onSelection,
  productOptionNumber,
  productOptionPrice,
  pricedProductFullOptions,
  useInactiveStyle,
  value,
  ...otherProps
}) => {
  const selectRef = useRef();
  const { formatMessage, formatNumber, formats } = useIntl();
  const [optionPrice, setOptionPrice] = useState('');

  useMoveFocus({ productOptionNumber, elementRef: selectRef });

  const selectedOptionFormatterHandler = (selectedOption) => {
    if (!isDemystifyMenuPdpEnabled) return selectedOption?.text;

    const formattedOptionPrice =
      productOptionPrice > 0
        ? formatNumber(productOptionPrice, formats.number.money)
        : '';
    setOptionPrice(formattedOptionPrice);
    return (
      <div className="flex justify-between items-center">
        <span>{selectedOption.text}</span>
        {formattedOptionPrice && (
          <span className="text-xs color-black60">{formattedOptionPrice}</span>
        )}
      </div>
    );
  };

  const renderOptionsWithPrices = () => {
    return Array.from(options).map(
      ([optionSku, { name: optionName, sizeCode: optionSizeCode }]) => {
        let currentOptionPrice = pricedProductFullOptions?.get(optionSku);

        if (optionSizeCode === 'no' && productOptionPrice > 0) {
          currentOptionPrice = productOptionPrice * -1;
        }
        const shouldShowPrice =
          isDemystifyMenuPdpEnabled && Math.abs(currentOptionPrice);

        const formattedOptionPrice = shouldShowPrice
          ? `, ${formatNumber(currentOptionPrice, formats.number.money)}`
          : '';

        return (
          <option
            aria-label={`${optionName}${formattedOptionPrice}`}
            key={optionName}
            value={optionName}
          >
            {optionName}
            {shouldShowPrice ? formattedOptionPrice : ''}
          </option>
        );
      }
    );
  };

  return (
    <div className="pb2" {...otherProps}>
      <Select
        aria-label={`${children} ${optionPrice}`}
        enableValidationMessage={false}
        id={id}
        label={children}
        modified={modified}
        onChange={(ev) => onSelection(ev.target.value)}
        ref={selectRef}
        secondaryLabel={
          modified ? formatMessage(commonMessages.customized) : null
        }
        selectedOptionFormatter={selectedOptionFormatterHandler}
        useInactiveStyle={useInactiveStyle}
        // if no value is currently selected, set the value to ''
        // so that the Select component does not go into an uncontrolled state
        // (as happens if you pass value={undefined}).
        // This prevents the component from blurring when it becomes controlled.
        value={value ?? ''}
      >
        {addNoneOption ? (
          <option key={`none-${children}`} value={NONE_VALUE}>
            {formatMessage(messages.none)}
          </option>
        ) : null}
        {renderOptionsWithPrices()}
      </Select>
    </div>
  );
};

SelectWrapper.propTypes = {
  children: PropTypes.node,
  options: PropTypes.object,
};

export default SelectWrapper;
